import { type ReactElement, type ReactNode } from 'react';
import { iconSizes } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, CircularProgress, Surface, Text } from '@/ui/primitives';
import { DataStateSkeleton } from '../DataStateSkeleton';
import { DataTableShell } from '../DataTableShell';
import { EmptyState } from '../EmptyState';
import { StatusBanner } from '../StatusBanner';
import type { ErrorInput } from '@/shared/data/errors';
import type { UiTone } from '@/theme/uiTokens';

type PageStateScaffoldProps = {
  session: object | null;
  sessionMessage: string;
  organisationId?: string | null;
  requireOrganisation?: boolean;
  organisationMessage?: string;
  projectId?: string | null;
  requireProject?: boolean;
  projectMessage?: string;
  requireAdmin?: boolean;
  isAdmin?: boolean;
  adminMessage?: string;
  adminFallback?: ReactNode;
  redirecting?: boolean;
  redirectMessage?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  loadingFallback?: ReactNode;
  isError?: boolean;
  error?: ErrorInput;
  errorMessage?: string;
  empty?: boolean;
  emptyMessage?: string;
  emptyTone?: UiTone;
  variant?: 'table' | 'card';
  rows?: number;
  children: ReactNode;
};

export const PageStateScaffold = ({
  session,
  sessionMessage,
  organisationId = null,
  requireOrganisation = false,
  organisationMessage,
  projectId = null,
  requireProject = false,
  projectMessage,
  requireAdmin = false,
  isAdmin = false,
  adminMessage,
  adminFallback,
  redirecting = false,
  redirectMessage,
  isLoading = false,
  loadingMessage,
  loadingFallback,
  isError = false,
  error,
  errorMessage,
  empty = false,
  emptyMessage,
  variant = 'card',
  rows,
  children,
}: PageStateScaffoldProps): ReactElement => {
  const renderPanel = (title: string, body?: string): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <EmptyState title={title} body={body} surface={false} />
        </DataTableShell>
      );
    }

    return <EmptyState title={title} body={body} />;
  };

  const renderSkeleton = (): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <DataStateSkeleton variant="table" rows={rows} padding={null} />
        </DataTableShell>
      );
    }

    return (
      <Surface padding="md">
        <DataStateSkeleton variant="card" rows={rows} padding={null} />
      </Surface>
    );
  };

  const renderLoadingState = (message: string): ReactElement => (
    <Box
      sx={{
        minHeight: '45vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack gap="md" align="center">
        <Text
          role="body"
          color="textSecondary"
          align="center"
          sx={{ fontSize: (theme) => theme.typography.subtitle1.fontSize }}
        >
          {message}
        </Text>
        <CircularProgress size={iconSizes.lg} />
      </Stack>
    </Box>
  );

  const renderError = (message: string): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <StatusBanner tone="red" message={message} />
        </DataTableShell>
      );
    }

    return (
      <Surface padding="md">
        <StatusBanner tone="red" message={message} />
      </Surface>
    );
  };

  if (!session) {
    return renderPanel(sessionMessage);
  }

  if (requireOrganisation && !organisationId) {
    return renderPanel(organisationMessage ?? 'Select an organisation to continue.');
  }

  if (requireProject && !projectId) {
    return renderPanel(projectMessage ?? 'Select a project to continue.');
  }

  if (isLoading) {
    if (loadingMessage) {
      return renderPanel(loadingMessage);
    }
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    return renderSkeleton();
  }

  if (requireAdmin && !isAdmin) {
    if (adminFallback) {
      return <>{adminFallback}</>;
    }
    return renderPanel(adminMessage ?? 'Admin access is required.');
  }

  if (redirecting) {
    return renderLoadingState(redirectMessage ?? 'Redirecting...');
  }

  if (isError) {
    const typedError = error as { message?: string } | null | undefined;
    const message = typedError?.message ?? errorMessage ?? 'Something went wrong.';
    return renderError(message);
  }

  if (empty) {
    return renderPanel(emptyMessage ?? 'Nothing here yet.');
  }

  return <>{children}</>;
};
